import { Button, Flex, Table, Card, List, Typography, Tag, Spin, message } from "antd";
import React, { useEffect, useState } from "react";
import { headersTableConfig, tableConfig } from "./config";
import { ISalary, ISalaryHeader } from "../../model/ISalary";
import { downloadFileWithBody, downloadFileWithParams, getRequestWithParams } from "../../../../api/httpRequest";
import { userStore } from "../../../../store/store";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useMediaQuery } from "react-responsive";
import { FaFilePdf } from "react-icons/fa";
import * as XLSX from "xlsx";

const importantRows = ["Начислено", "Всего начислено", "Удержано", "Всего удержано", "К выдаче"];
export const MASKING = `${process.env.MASKING}`;

export default function Accruals() {
    const [salaryHeaders, setSalaryHeaders] = useState<ISalaryHeader[]>([]);
    const { user } = userStore();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedAccruals, setSelectedAccruals] = useState<ISalary | null>(null);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [exportingId, setExportingId] = useState<number | null>(null);

    const isMobile = useMediaQuery({ maxWidth: 768 });

    // Существующая функция загрузки деталей (сохраняем без изменений)
    const fetchAccrualDetails = (header: ISalaryHeader, index: number) => {
        if (selectedId === index && selectedAccruals) return;
        setIsDetailsLoading(true);
        getRequestWithParams("/server/proxy/salary/payroll/details/date", {
            numb: header.NUMB,
            date: header.EMP_CALC,
        })
            .then((result) => {
                const details = result?.data?.result?.[0];
                if (!details) {
                    console.warn("Нет данных о деталях начисления");
                    setIsDetailsLoading(false);
                    return;
                }

                let data;
                if (MASKING === "1") {
                    data = {
                        ...details,
                        data: details.data.map((item: ISalary) => ({
                            ...item,
                            PAYS_SUMM: (Math.floor(Math.random() * 1000) + 1).toString(),
                        })),
                    };
                } else {
                    data = details;
                }
                setSelectedAccruals(data);
                setSelectedId(index);
                setIsDetailsLoading(false);
            })
            .catch((error) => {
                console.error("Ошибка загрузки деталей начисления:", error);
                setIsDetailsLoading(false);
            });
    };

    useEffect(() => {
        const fetchSalaryHeaders = () => {
            setIsLoading(true);
            dayjs().locale("ru");
            getRequestWithParams("/server/proxy/salary/payroll/headers", {})
                .then((result) => {
                    const rawData = result?.data?.result;
                    if (!Array.isArray(rawData)) {
                        console.warn("Некорректный ответ от API:", result);
                        setSalaryHeaders([]);
                        setIsLoading(false);
                        return;
                    }

                    const salaryHeadersResponse = rawData.sort(
                        (item1: ISalaryHeader, item2: ISalaryHeader) =>
                            dayjs(item1.EMP_CALC).isAfter(dayjs(item2.EMP_CALC)) ? -1 : 1
                    );

                    if (MASKING === "1") {
                        setSalaryHeaders(
                            salaryHeadersResponse.map((item: ISalaryHeader) => ({
                                ...item,
                                PAYS_SUMM: (Math.floor(Math.random() * 100) + 1).toString(),
                            }))
                        );
                    } else {
                        setSalaryHeaders(salaryHeadersResponse);
                    }

                    if (salaryHeadersResponse.length > 0) {
                        fetchAccrualDetails(salaryHeadersResponse[0], 0);
                    }

                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Ошибка загрузки заголовков зарплаты:", error);
                    setIsLoading(false);
                });
        };

        fetchSalaryHeaders();
    }, []);

    // Функция для получения деталей без изменения выбранной строки (используется только для экспорта)
    const fetchDetailsForExport = (header: ISalaryHeader, index: number): Promise<ISalary> => {
        // Если уже загружены и это та же строка — используем
        if (selectedId === index && selectedAccruals) {
            return Promise.resolve(selectedAccruals);
        }
        // Иначе загружаем отдельно
        return getRequestWithParams("/server/proxy/salary/payroll/details/date", {
            numb: header.NUMB,
            date: header.EMP_CALC,
        }).then((result) => {
            const details = result?.data?.result?.[0];
            if (!details) {
                throw new Error("Нет данных о деталях начисления");
            }
            if (MASKING === "1") {
                return {
                    ...details,
                    data: details.data.map((item: ISalary) => ({
                        ...item,
                        PAYS_SUMM: (Math.floor(Math.random() * 1000) + 1).toString(),
                    })),
                };
            }
            return details;
        });
    };

    // Функция экспорта PDF
    const handleExportPdf = async (header: ISalaryHeader, index: number) => {
        setExportingId(index);
        try {
            // Получаем детали расчёта (используем уже загруженные, если возможно)
            const details = await fetchDetailsForExport(header, index);
            if (!details.data || details.data.length === 0) {
                message.error("Нет данных для формирования PDF");
                return;
            }

            // Формируем тело запроса
            const fio = user.lastName + " " + user.firstName + " " + user.middleName;
            const monthYear = dayjs(header.EMP_CALC).locale("ru").format("MMMM YYYY");
            const insDep = header.INS_DEP;
            const postCode = header.POST_CODE;

            const pays = details.data.map((item) => ({
                paysName: item.PAYS_NAME,
                paysSht: item.PAYS_SHT,
                paysBegEnd: item.PAYS_BEGEND,
                paysWrk: item.PAYS_WRK,
                paysPrc: item.PAYS_PRC,
                paysSum: item.PAYS_SUMM ? Number(item.PAYS_SUMM?.toString().replace(',', '.')).toLocaleString() : "",
            }));

            const requestBody = {
                fio,
                insDep,
                postCode,
                monthYear,
                pays,
            };

            downloadFileWithBody("/server/pdf/salary", requestBody)
            .then(response => {
                const href = URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', `Рассчёты за ${monthYear}`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch(error => {
                console.error("Ошибка экспорта PDF:", error);
                message.error("Не удалось сгенерировать PDF");
            })
        
        } catch (error) {
            console.error("Ошибка экспорта PDF:", error);
            message.error("Не удалось сгенерировать PDF");
        } finally {
            setExportingId(null);
        }
    };

    const renderDetailsList = (data: ISalary["data"]) => {
        if (!data) return null;
        return (
            <List
                size="small"
                dataSource={data}
                renderItem={(item) => {
                    const isImportant = importantRows.indexOf(item.PAYS_NAME) > -1;
                    return (
                        <List.Item
                            style={{
                                backgroundColor: isImportant ? "#fff1f0" : "transparent",
                                fontWeight: isImportant ? "bold" : "normal",
                                padding: "8px 12px",
                                borderBottom: "1px solid #f0f0f0",
                            }}
                        >
                            <Flex justify="space-between" style={{ width: "100%" }}>
                                <span>{item.PAYS_NAME}</span>
                                <span>{item.PAYS_SUMM} ₽</span>
                            </Flex>
                        </List.Item>
                    );
                }}
            />
        );
    };

    const renderSalaryCards = () => (
        <List
            loading={isLoading}
            dataSource={salaryHeaders}
            renderItem={(item, index) => {
                const isOpen = selectedId === index;
                const isExporting = exportingId === index;
                return (
                    <Card
                        key={index}
                        hoverable
                        style={{
                            marginBottom: 12,
                            border: isOpen ? "1px solid #1890ff" : "1px solid #d9d9d9",
                            backgroundColor: isOpen ? "#f5f5f5" : "white",
                        }}
                    >
                        <Flex justify="space-between" align="center">
                            <div onClick={() => fetchAccrualDetails(item, index)} style={{ flex: 1 }}>
                                <Typography.Text strong>
                                    {dayjs(item.EMP_CALC).locale("ru").format("MMMM YYYY")}
                                </Typography.Text>
                                <br />
                                <Typography.Text type="secondary">
                                    {item.POST_CODE} • {item.INS_DEP}
                                </Typography.Text>
                            </div>
                            <Flex gap={8} align="center">
                                <Tag color="green">{item.PAYS_SUMM} ₽</Tag>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<FaFilePdf/>}
                                    loading={isExporting}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportPdf(item, index);
                                    }}
                                >
                                    PDF
                                </Button>
                            </Flex>
                        </Flex>

                        {isOpen && (
                            <div style={{ marginTop: 16, borderTop: "1px solid #e8e8e8", paddingTop: 12 }}>
                                {isDetailsLoading ? (
                                    <Flex justify="center">
                                        <Spin />
                                    </Flex>
                                ) : (
                                    selectedAccruals && renderDetailsList(selectedAccruals.data)
                                )}
                            </div>
                        )}
                    </Card>
                );
            }}
        />
    );

    const handleExportExcel = () => {
        const exportData = salaryHeaders.map((item) => ({
            "Должность": item.POST_CODE,
            "Подразделение": item.INS_DEP,
            "Год": dayjs(item.EMP_CALC).year().toString(),
            "Месяц": dayjs(item.EMP_CALC).locale("ru").format("MMMM").toString(),
            "Начислено": item.PAYS_SUMM,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Начисления");
        XLSX.writeFile(workbook, "Расчетные листки.xlsx");
    };

    return (
        <Flex vertical gap={8} style={{ padding: isMobile ? 8 : 16 }}>
            <Typography.Text>
                Для просмотра доступна информация о выплатах в кассе, произведённых после 04.04.2013 г.
            </Typography.Text>

            <Flex justify="space-between" align="center" wrap="wrap">
                <Typography.Text className="title-text">Расчетные листки</Typography.Text>
                <Button
                    type="text"
                    icon={<RiFileExcel2Fill color="#40a202" size={20} />}
                    onClick={handleExportExcel}
                >
                    Экспорт в XLSX
                </Button>
            </Flex>

            {isMobile ? (
                renderSalaryCards()
            ) : (
                <>
                    <Table
                        style={{ border: "1px #a6a6a6 solid", borderStyle: "none solid solid none" }}
                        bordered
                        loading={isLoading}
                        size="small"
                        pagination={false}
                        dataSource={salaryHeaders}
                        scroll={{ y: 200 }}
                        columns={headersTableConfig(salaryHeaders, handleExportPdf)}
                        onRow={(record, rowIndex) => ({
                            onClick: () => {
                                if (rowIndex !== undefined) {
                                    setSelectedId(rowIndex);
                                    fetchAccrualDetails(record, rowIndex);
                                }
                            },
                        })}
                        rowClassName={(_, index) =>
                            index === selectedId ? "selected-row" : index % 2 === 0 ? "" : "darker-row"
                        }
                    />

                    {selectedAccruals && (
                        <>
                            <Typography.Text className="title-text">
                                {`Расчеты за ${dayjs(selectedAccruals.EMP_CALC).locale("ru").format("MMMM")} ${dayjs(selectedAccruals.EMP_CALC).year()} года`}
                            </Typography.Text>
                            <Table
                                style={{ border: "1px #a6a6a6 solid", borderStyle: "none solid solid none" }}
                                bordered
                                size="small"
                                pagination={false}
                                loading={isDetailsLoading}
                                columns={tableConfig}
                                scroll={{ y: 450 }}
                                dataSource={selectedAccruals?.data}
                                rowClassName={(record) =>
                                    importantRows.indexOf(record.PAYS_NAME) > -1 ? "important-row" : ""
                                }
                            />
                        </>
                    )}
                </>
            )}
        </Flex>
    );
}